<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { GroupPortholes } from '@client/components/group-portholes';
  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SenderPortholes } from '@client/components/sender-portholes';
  import { SignaturePreview } from '@client/components/signature-preview';

  import { GroupsService } from '@client/services/groups';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { generateUniqueRulename } from '@client/utils/rulenames';

  const { addNotification } = getNotificationsContext();

  export let data = {};
  export let refetch = () => {};
  export let onRename = null;
  export let excludedNames = {};

  let showUserMenu = false;
  let dropdownRef = null;
  let showPreview = false;
  let active = false;

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onMouseEnter = async () => {
    showPreview = true;
  };

  const onMouseLeave = () => {
    showPreview = false;
  };

  const onClick = (e) => {
    const element = e.target;

    active = true;

    if (dropdownRef === element || dropdownRef.contains(element)) {
      active = false;
    } else {
      navigate(`/manage/groups/${data.groupname}`, {});
    }
  };

  const onMouseUp = () => {
    active = false;
  };

  const onDelete = async () => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.GROUP').toLowerCase() } })
    );

    if (confirmed) {
      $appStore.loading = true;

      const response = await GroupsService.deleteGroup(data);

      $appStore.loading = false;

      if (response !== 'Empty' && response?.status >= 400) {
        notify($format('label.FAILURE.GROUP_DELETE', { values: { group: data.groupname } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: data.groupname } }));

      refetch();
    }
  };

  const onClone = async () => {
    $appStore.loading = true;

    const groupname = generateUniqueRulename(data.groupname);

    const payload = {
      ...data,
      _id: null,
      _newObject: true,
      groupname
    };

    const response = await GroupsService.createGroup(payload);

    $appStore.loading = false;

    if (response !== 'Empty' && response?.status >= 400) {
      notify($format('label.FAILURE.GROUP_CLONE', { values: { group: data.groupname } }), 'error');
      return;
    }

    notify($format('label.SUCCESS.GROUP_CLONE', { values: { group: data.groupname } }));

    setTimeout(() => navigate(`/manage/groups/${payload.groupname}`), 200);
  };

  const toUsers = (list) => (list || []).map((u) => ({ username: u.username }));
  const normBinding = (b) => ({ name: b.name ?? b.displayName ?? '', count: b.directMembersCount });
  const normManager = (m) => ({ name: [m.first, m.last].filter(Boolean).join(' '), count: m.direct_reports });

  // Excluded bindings are bare ids; resolve their name/count from the
  // page-supplied map, falling back to whatever the binding itself carries.
  const hydrateExcluded = (binding, map) => {
    const hit = map[binding.id] ?? map[binding.employee_id] ?? map[binding.uuid];
    return hit ? { name: hit.name, count: hit.count } : normBinding(binding);
  };

  // Display-only view model: two buckets (Users, Groups), each a single merged
  // list of included + excluded items. Excluded items carry `excluded: true` so
  // the porthole gives them a red ring. All arrays are freshly mapped, so the
  // porthole components' in-place sort never mutates `data`.
  $: usersBucket = [
    ...(data.userAttrs?.length ? [{ dynamic: true, excluded: false }] : []),
    ...toUsers(data.senders).map((u) => ({ ...u, excluded: false })),
    ...toUsers(data.senderexclusions).map((u) => ({ ...u, excluded: true }))
  ];

  $: groupsBucket = [
    ...[
      ...(data.googlegroupbindings || []),
      ...(data.o365groupbindings || []),
      ...(data.oktagroupbindings || []),
      ...(data.googleorgbindings || [])
    ].map((b) => ({ ...normBinding(b), excluded: false })),
    ...(data.csvtreenodebindings || []).map((m) => ({ ...normManager(m), excluded: false })),
    ...[
      ...(data.exclusions?.googlegroupbindings || []),
      ...(data.exclusions?.o365groupbindings || []),
      ...(data.exclusions?.oktagroupbindings || []),
      ...(data.exclusions?.googleorgbindings || [])
    ].map((b) => ({ ...hydrateExcluded(b, excludedNames), excluded: true })),
    ...(data.exclusions?.csvtreenodebindings || []).map((m) => ({
      ...hydrateExcluded(m, excludedNames),
      excluded: true
    }))
  ];

  $: type = data?.sigparams?.length ? $format('label.SIGNATURE') : $format('label.BANNER');
  $: isActive = data.status;
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="group-card"
  class:active
  class:inactive={!isActive}
  on:click={onClick}
  on:mouseleave={() => (showUserMenu = false)}
  on:mouseup={onMouseUp}
  role="button"
  tabindex="0"
>
  <div bind:this={dropdownRef} class="action">
    <Dropdown isOpen={showUserMenu} toggle={() => (showUserMenu = !showUserMenu)}>
      <DropdownToggle tag="div" class="os-icon-more-horizontal" />
      <DropdownMenu>
        {#if onRename}
          <DropdownItem on:click={() => onRename(data)}>{$format('label.RENAME')}</DropdownItem>
          <DropdownItem divider />
        {/if}
        <DropdownItem on:click={() => redirect(`/manage/groups/${data.groupname}`)}>
          {$format('label.EDIT')}
        </DropdownItem>
        <DropdownItem on:click={onClone}>{$format('label.CLONE')}</DropdownItem>
        <DropdownItem divider />
        <DropdownItem disabled={isActive} on:click={onDelete}>{$format('label.DELETE')}</DropdownItem>
      </DropdownMenu>
    </Dropdown>
  </div>
  <div class="group">
    <div class="status-row">
      {#if isActive}
        <div class="active">{$format('label.ROUTED')}</div>
      {:else}
        <div class="spacer"></div>
      {/if}

      {#if data?.outlookaddin?.enableOverrides}
        <span class="override" id="group-override-{data.groupname}">
          <Icon name="signpost" size={14} />
        </span>
        <Tooltip target="group-override-{data.groupname}" placement="top">
          {$format('label.ADDIN_OVERRIDES')}
        </Tooltip>
      {/if}
    </div>

    <div class="section name">
      <small>{$format('label.GROUP')}</small>
      <div class="group-badge" style="background-color: {data.color};">{data.groupname}</div>
    </div>

    <div class="section signature">
      {#if data.blockname}
        <small>{type}</small>
        <div
          class="signature-badge"
          on:mouseenter={onMouseEnter}
          on:mouseleave={onMouseLeave}
          role="button"
          tabindex="0"
        >
          {data.blockname}

          <SignaturePreview
            blockname={data.blockname}
            bind:show={showPreview}
            user={{
              email:
                !data.senders[0] || data.senders[0].username === '*'
                  ? `adminuser@${$userStore.domain}`
                  : `${data.senders[0].username}@${$userStore.domain}`
            }}
          />
        </div>
      {:else}
        <small>&nbsp;</small>
        <p class="muted">{$format('label.NO_ASSIGNED_SIGNATURE')}</p>
      {/if}
    </div>

    {#if usersBucket.length || groupsBucket.length}
      <div class="assignments">
        {#if usersBucket.length}
          <div class="bucket">
            <small>{$format('label.USERS')}</small>
            <SenderPortholes data={{ groupname: `${data.groupname}-users`, senders: usersBucket }} type="senders" />
          </div>
        {/if}
        {#if groupsBucket.length}
          <div class="bucket">
            <small>{$format('label.GROUPS')}</small>
            <GroupPortholes data={{ groups: groupsBucket }} type="groups" nameField="name" countField="count" />
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="scss" src="./group-card.scss"></style>
